/**
 * Copyright 2013-present, Novivia, Inc.
 * All rights reserved.
 */

import AENavBrand from "./NavBrand";
import AENavHelp from "./NavHelp";
import AENavLanguagePicker from "./NavLanguagePicker";
import AENavLogout from "./NavLogout";
import BSCol from "react-bootstrap/Col";
import BSGlyphicon from "react-bootstrap/Glyphicon";
import BSGrid from "react-bootstrap/Grid";
import BSMenuItem from "react-bootstrap/MenuItem";
import BSNav from "react-bootstrap/Nav";
import BSNavbar from "react-bootstrap/Navbar";
import BSNavDropdown from "react-bootstrap/NavDropdown";
import BSNavItem from "react-bootstrap/NavItem";
import BSRow from "react-bootstrap/Row";
import {decodeEntities} from "security";
import FAIcon from "react-fa/Icon";
import {getTimezoneInfo, localizeDate} from "datetime";
import {noWhitespace} from "tempura";
import PropTypes from "prop-types";
import React, {Component} from "react";
import {oneLine} from "common-tags";

export default class Application extends Component {
  static propTypes = {
    assetsUrl: PropTypes.string.isRequired,
    auctionCategories: PropTypes.array.isRequired,
    children: PropTypes.any.isRequired,
    features: PropTypes.object.isRequired,
    languages: PropTypes.object.isRequired,
    sessionLanguage: PropTypes.string.isRequired,
    tenantId: PropTypes.string.isRequired,
    user: PropTypes.object,
  };

  static contextTypes = {
    __: PropTypes.func.isRequired,
  };

  renderOrganizations = () => {
    const {
      __,
    } = this.context;

    const {
      features,
      user,
    } = this.props;

    if (!user || !user.organizations.length) {
      if (features.privateTenant) {
        return null;
      }

      return [
        <BSNavItem
          href={`/${__("routes::buying")}`}
          key="buying"
        >
          <FAIcon name="hand-o-up" /> {__("menu::buying")}
        </BSNavItem>,
        <BSNavItem
          href={`/${__("routes::selling")}`}
          key="selling"
        >
          <FAIcon name="certificate" /> {__("menu::selling")}
        </BSNavItem>,
      ];
    }

    const {organizations} = user;

    if (organizations.length === 1) {
      const {friendlyId} = organizations[0];

      return (
        <BSNavItem
          href={noWhitespace`
            /${__("routes::selleraccount")}
            /${friendlyId}
          `}
        >
          <BSGlyphicon glyph="dashboard" /> {__("menu::sellerDashboard")}
        </BSNavItem>
      );
    }

    return (
      <BSNavDropdown
        id="menu-organization-picker"
        title={
          <span>
            <BSGlyphicon glyph="dashboard" />
            {" "}
            {__("menu::sellerDashboard", {count: organizations.length})}
          </span>
        }
      >
        {organizations.map(({friendlyId, name}, index) => (
          <BSMenuItem
            href={noWhitespace`
              /${__("routes::selleraccount")}
              /${friendlyId}
            `}
            key={index}
          >
            {decodeEntities(name)}
          </BSMenuItem>
        ))}
      </BSNavDropdown>
    );
  };

  render() {
    const {
      __,
    } = this.context;

    const {
      assetsUrl,
      auctionCategories,
      brand,
      features,
      languages,
      sessionLanguage,
      tenantId,
      user,
    } = this.props;

    const timezoneInfo = getTimezoneInfo();

    return (
      <div id="main">
        <BSNavbar>
          <BSNavbar.Header>
            <AENavBrand
              assetsUrl={assetsUrl}
              brand={brand}
              tenantId={tenantId}
            />
            <BSNavbar.Toggle />
          </BSNavbar.Header>
          <BSNavbar.Collapse>
            <BSNav>
              <BSNavItem href={`/${__("routes::auctions")}`}>
                <FAIcon name="gavel" /> {__("auction::auctions")}
              </BSNavItem>
              {features.sellerDirectory ? (
                <BSNavItem href={`/${__("routes::directory")}`}>
                  <FAIcon name="book" />
                  {` ${__("organization::listOrganizations.header")}`}
                </BSNavItem>
              ) : null}
              {!features.privateTenant ? (
                <BSNavDropdown
                  id="menu-category-picker"
                  title={
                    <span>
                      <FAIcon name="bars" /> {__("menu::categories")}
                    </span>
                  }
                >
                  {auctionCategories.map(({id}) => (
                    <BSMenuItem
                      href={noWhitespace`
                        /${__("routes::auctions")}
                        /${__("routes::category")}
                        /${id}
                      `}
                      key={id}
                    >
                      {__(`categories::${id}`)}
                    </BSMenuItem>
                  ))}
                </BSNavDropdown>
              ) : null}
              {this.renderOrganizations()}
            </BSNav>
            <BSNav pullRight>
              {user ? [
                <BSNavItem
                  href={`/${__("routes::dashboard")}`}
                  key="dashboard"
                >
                  <FAIcon name="user" /> {__("menu::myAccount.header")}
                </BSNavItem>,
                <AENavLogout key="logout" />,
              ] : [
                <BSNavItem
                  href={`/${__("routes::login")}`}
                  key="login"
                >
                  <FAIcon name="sign-in" /> {__("login::header")}
                </BSNavItem>,
                <BSNavItem
                  href={`/${__("routes::signup")}`}
                  key="signup"
                >
                  <FAIcon name="user" /> {__("login::register")}
                </BSNavItem>,
              ]}
              <AENavLanguagePicker
                languages={languages}
                sessionLanguage={sessionLanguage}
              />
              {features.helpCenter ? <AENavHelp /> : null}
            </BSNav>
          </BSNavbar.Collapse>
        </BSNavbar>

        {this.props.children}

        <footer>
          <BSGrid>
            <BSRow>
              <BSCol md={3} sm={3}>
                <h5>{__("footer::productsAndServices.header")}</h5>

                <ul>
                  {!features.privateTenant ? (
                    <li>
                      <a href={`/${__("routes::selling")}`}>
                        {__("footer::productsAndServices.selling")}
                      </a>
                    </li>
                  ) : null}
                  <li>
                    <a href={`/${__("routes::buying")}`}>
                      {__("footer::productsAndServices.buying", {brand})}
                    </a>
                  </li>
                  {!features.privateTenant ? (
                    <li>
                      <a href={`/${__("routes::features")}`}>
                        {__("footer::productsAndServices.features")}
                      </a>
                    </li>
                  ) : null}
                  {!features.privateTenant ? (
                    <li>
                      <a href={`/${__("routes::pricing")}`}>
                        {__("footer::productsAndServices.pricing")}
                      </a>
                    </li>
                  ) : null}
                  <li>
                    <a href={`/${__("routes::terminology")}`}>
                      {__("terminology::header")}
                    </a>
                  </li>

                  {features.helpCenter ? (
                    <li>
                      <a href={`/${__("routes::help")}`}>
                        {__("footer::help")}
                      </a>
                    </li>
                  ) : null}
                </ul>
              </BSCol>
              <BSCol md={3} sm={3}>
                <h5>{__("footer::about.header")}</h5>

                <ul>
                  {!features.privateTenant ? (
                    <li>
                      <a href={`/${__("routes::about")}`}>
                        {__("footer::about.aboutUs")}
                      </a>
                    </li>
                  ) : null}
                  {!features.privateTenant ? (
                    <li>
                      <a href={`/${__("routes::press")}`}>
                        {__("press::header")}
                      </a>
                    </li>
                  ) : null}
                  <li>
                    <a href={`/${__("routes::privacy")}`}>
                      {__("footer::about.privacy")}
                    </a>
                  </li>
                  <li>
                    <a href={`/${__("routes::terms")}`}>
                      {__("general::terms")}
                    </a>
                  </li>
                </ul>
              </BSCol>
              <BSCol md={3} sm={3}>
                {!features.privateTenant ? (
                  <div>
                    <h5>{__("footer::community.header")}</h5>

                    <ul>
                      <li>
                        <a href="//blog.novivia.com/tag/auctionex">
                          Blog
                        </a>
                      </li>
                      <li>
                        <a href="//twitter.com/auctionexen">
                          Twitter
                        </a>
                      </li>
                      <li>
                        <a href="//facebook.com/auctionex">
                          Facebook
                        </a>
                      </li>
                      <li>
                        <a href={`/${__("routes::contact")}`}>
                          {__("contact::header")}
                        </a>
                      </li>
                    </ul>
                  </div>
                ) : null}
              </BSCol>
              <BSCol md={3} sm={3}>
                <p>
                  &copy;
                  {" "}
                  {oneLine`
                    ${localizeDate({format: "YYYY"}).date}
                    ${features.privateTenant ? __("general::poweredBy") : ""}
                    Auctionex, Inc.
                  `}
                </p>
                <a href="//ssl.comodo.com/wildcard-ssl-certificates.php">
                  <img
                    alt="Wildcard SSL"
                    className="comodo-seal"
                    src={`${assetsUrl}/img/comodo-seal.png`}
                  />
                </a>

                <div className="user-timezone-info">
                  <BSGlyphicon glyph="time" />
                  {` ${__("footer::OSTimezone")}: UTC `}
                  {`${timezoneInfo.offsetString} ${timezoneInfo.abbreviation}`}
                </div>
              </BSCol>
            </BSRow>
          </BSGrid>
        </footer>
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ../render/components/Application.jsx